import { CalendarIcon,  Filter,   Search, Loader2, ChevronDown, Check } from "lucide-react";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { useNavigate, useLocation } from "react-router-dom";
import MobileSlider from "../../../../components/ui/mobile-slider";
import NoDataComponent from "../../../../components/infoTemplateIcons/noData";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { predictionsApi, ApiResponse, PredictionsResponse, Product } from "../../../../api/predictionsApi";
import { Prediction } from "../../../../lib/api/predictions";
import { toast } from "react-hot-toast";
import { Loader } from "../../../../components/ui/loader";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Input } from "../../../../components/ui/input";
import { PREDICTIONS_PAGE_LIMIT, PREDICTION_STATUS_OPTIONS, PREDICTION_STATUS_CLASSES } from '../../../../constants';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { COUNTRY_DATA } from '../../../../utils/countryCode';

interface PredictionsTableSectionProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
  onTotalItemsChange: (total: number) => void;
  selectedProduct: string;
  setSelectedProduct: React.Dispatch<React.SetStateAction<string>>;
  selectedDateFilter: string;
  setSelectedDateFilter: React.Dispatch<React.SetStateAction<string>>;
  selectedStatus: string;
  setSelectedStatus: React.Dispatch<React.SetStateAction<string>>;
  globalSearchQuery: string;
  setGlobalSearchQuery: React.Dispatch<React.SetStateAction<string>>;
}

// Add ReadOnlyInput component
const ReadOnlyInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  (props, ref) => (
    <input
      {...props}
      ref={ref}
      readOnly
    />
  )
);

export const PredictionsTableSection = ({
  currentPage,
  onPageChange,
  onTotalItemsChange,
  selectedProduct,
  setSelectedProduct,
  selectedDateFilter,
  setSelectedDateFilter,
  selectedStatus,
  setSelectedStatus,
  globalSearchQuery,
  setGlobalSearchQuery,
}: PredictionsTableSectionProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const countryMatch = location.pathname.match(/^\/([a-z]{2,})\//i);
  const country = countryMatch ? countryMatch[1] : "in";
  const userId = useSelector((state: RootState) => state.user.userId);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const token = useSelector((state: RootState) => state.user.token);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const limit = PREDICTIONS_PAGE_LIMIT;
  const lastFetchedPageRef = useRef(0);
  const filtersRef = useRef({
    productId: "",
    status: "",
    date: "",
    search: "",
  });
  const isApplyingFiltersRef = useRef(false);

  const handleCardClick = (predictionId: string) => {
    navigate(`/${country}/prediction-details`, { 
      state: { 
        id: predictionId,
        returnPage: currentPage 
      } 
    });
  };

  // Filter products based on product search query
  const filteredProducts = useMemo(() => 
    products.filter((product) =>
      product.name.toLowerCase().includes(productSearchQuery.toLowerCase())
    ), [products, productSearchQuery]
  );

  const countryObj = COUNTRY_DATA[country] || { timeZone: 'UTC' };
  const timeZone = countryObj.timeZone;
  const dateFormatOptions: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric', timeZone };
  const predictionCards = Array.isArray(predictions) ? predictions.map(prediction => ({
    id: prediction.id,
    logoSrc: prediction?.product?.logo,
    logoAlt: prediction?.product?.name,
    name: prediction?.name || "--",
    status: (prediction?.status || "--") as string,
    landPreparedDate: prediction?.attributes?.land_preparation_date ? format(new Date(prediction.attributes.land_preparation_date), 'd MMM yyyy') : "--",
    transplantDate: prediction?.attributes?.transplant_date ? format(new Date(prediction.attributes.transplant_date), 'd MMM yyyy') : "--",
    createdAt: prediction?.createdAt ? new Date(prediction.createdAt).toLocaleDateString('en-GB', dateFormatOptions) : "--",
    applicationRecommendation: prediction?.recommendations?.recommendation_date ? format(new Date(prediction.recommendations.recommendation_date), 'd MMM yyyy') : "--"
  })) : [];

  const fetchProducts = async () => {
    try {
      if (!token) {
        toast.error(t('authenticationRequired'));
        return;
      }
      setIsLoading(true);
      const response = await predictionsApi.getProducts() as unknown as ApiResponse<Product[]>;
      if (response && response.data && Array.isArray(response.data)) {
        setProducts(response.data);
      } else {
        setProducts([]);
        toast.error(t('invalidResponseFormat'));
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('failedToFetchProducts'));
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchPredictions = async (filters: {
    productId?: string;
    status?: string;
    date?: string;
    search?: string;
    page?: number;
    limit?: number;
  } = {}) => {
    try {
      if (!userId || !token) return;
      setIsLoading(true);
      const response = await predictionsApi.getUserPredictions(userId, {
        ...filters,
        page: filters.page || currentPage,
        limit
      });
      if (response && response.data) {
        setPredictions(response.data.predictions);
        onTotalItemsChange(response.data.total);
      } else {
        setPredictions([]);
        onTotalItemsChange(0);
        toast.error(t('invalidResponseFormat'));
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || t('failedToFetchPredictions');
      toast.error(errorMessage);
      setPredictions([]);
      onTotalItemsChange(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    
    // Skip if we're currently applying filters
    if (isApplyingFiltersRef.current) {
      return;
    }
    
    if (userId && currentPage > 0 && currentPage !== lastFetchedPageRef.current) {
      const filtersToSend = {
        ...filtersRef.current,
        page: currentPage,
        limit
      };
      fetchPredictions(filtersToSend);
      lastFetchedPageRef.current = currentPage;
    } else {
    }
  }, [userId, currentPage]);

  const handleApplyFilters = () => {
    const newFilters = {
      productId: selectedProduct,
      status: selectedStatus,
      date: selectedDateFilter,
      search: globalSearchQuery,
    };
    
    
    // Set flag to prevent useEffect from running
    isApplyingFiltersRef.current = true;
    
    // Always update filtersRef.current when Apply button is clicked
    filtersRef.current = newFilters;
    
    onPageChange(1);
    
    fetchPredictions({
      ...newFilters,
      page: 1,
      limit
    });
    
    // Reset flag after a short delay
    setTimeout(() => {
      isApplyingFiltersRef.current = false;
    }, 100);
    
    setSliderOpen(false);
  };

  const handleClearAll = () => {
    setDate(undefined);
    setSelectedDateFilter("");
    setSelectedStatus("");
    setSelectedProduct("");
    setProductSearchQuery("");
    setGlobalSearchQuery("");
    filtersRef.current = {
      productId: "",
      status: "",
      date: "",
      search: "",
    };
    onPageChange(1);
    fetchPredictions({
      page: 1,
      limit
    });
  };

  const handleNewPrediction = () => {
    navigate(`/${country}/new-prediction`, { state: { fromButton: true } });
  };

  const handleProductSearch = useCallback((value: string) => {
    setProductSearchQuery(value);
  }, []);

  const handleProductSelect = useCallback((value: string) => {
    setSelectedProduct(value);
    setProductSearchQuery("");
    setIsProductDropdownOpen(false);
  }, [setSelectedProduct]);

  const [sliderOpen, setSliderOpen] = useState(false);
  
  const FiltersContent = ({ isMobile = false }) => {
    return (
      <div className="flex items-center gap-2 flex-wrap flex-col sm:flex-row">
        <div className="w-full sm:w-auto flex items-center gap-2 flex-col sm:flex-row">
          {/* Custom Product Dropdown */}
          <div className="relative w-full sm:min-w-[180px]">
            <button
              type="button"
              className="flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-[#c2c7d0] bg-white px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => setIsProductDropdownOpen(!isProductDropdownOpen)}
              data-testid={isMobile ? "mobile-herbicide-select" : "desktop-herbicide-select"}
            >
              <div className="flex items-center gap-2">
                {selectedProduct && products.find(p => p.id.toString() === selectedProduct)?.attributes?.icon && (
                  <img
                    src={products.find(p => p.id.toString() === selectedProduct)?.attributes?.icon}
                    alt=""
                    className="w-5 h-5 object-contain"
                  />
                )}
                <span className={!selectedProduct ? 'text-gray-400' : ''}>
                  {selectedProduct 
                    ? products.find(p => p.id.toString() === selectedProduct)?.name || t('selectHerbicide')
                    : t('selectHerbicide')
                  }
                </span>
              </div>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </button>
            
            {isProductDropdownOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-[#c2c7d0] rounded-md shadow-lg max-h-60 overflow-hidden">
                {/* Search Input */}
                <div className="sticky top-0 bg-white p-2 border-b border-[#c2c7d0]">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input
                      className="pl-8 h-9"
                      placeholder="Search products..."
                      value={productSearchQuery}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleProductSearch(e.target.value)}
                      autoComplete="off"
                      autoFocus
                    />
                  </div>
                </div>
                
                {/* Options */}
                <div className="max-h-48 overflow-y-auto">
                  {isLoading ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Loading...
                    </div>
                  ) : error ? (
                    <div className="flex items-center justify-center p-4 text-red-500">
                      {error}
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="flex items-center justify-center p-4 text-gray-500">
                      {productSearchQuery ? 'No products found' : 'No products available'}
                    </div>
                  ) : (
                    filteredProducts.map((product) => (
                      <button
                        key={`product-${product.id}`}
                        type="button"
                        className={`w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${
                          selectedProduct === product.id.toString() ? 'bg-gray-50' : ''
                        }`}
                        onClick={() => handleProductSelect(product.id.toString())}
                      >
                        <div className="flex items-center gap-2">
                          {product.attributes?.icon && (
                            <img
                              src={product.attributes.icon}
                              alt=""
                              className="w-5 h-5 object-contain"
                            />
                          )}
                          <span>{product.name}</span>
                        </div>
                        {selectedProduct === product.id.toString() && (
                          <Check className="h-4 w-4 text-green-600" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
            
            {/* Overlay to close dropdown when clicking outside */}
            {isProductDropdownOpen && (
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => {
                  setIsProductDropdownOpen(false);
                  setProductSearchQuery("");
                }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Escape') {
                    setIsProductDropdownOpen(false);
                    setProductSearchQuery("");
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="Close product dropdown"
              />
            )}
          </div>

          <div className="flex flex-col w-full items-start gap-2 my-[16px] sm:my-0">
            <div className="relative w-full bg-white rounded-lg ">
              <DatePicker
                selected={date}
                onChange={(selectedDate: Date | null) => {
                  if (selectedDate) {
                    selectedDate.setHours(12, 0, 0, 0);
                    setDate(selectedDate);
                    setSelectedDateFilter(selectedDate.toISOString().split('T')[0]);
                  } else {
                    setDate(undefined);
                    setSelectedDateFilter("");
                    // Apply remaining filters when date is cleared
                    const newFilters = {
                      productId: selectedProduct,
                      status: selectedStatus,
                      date: "",
                      search: globalSearchQuery,
                    };
                    // Update filtersRef.current with remaining filters
                    filtersRef.current = newFilters;
                    // Make API call with remaining filters
                    fetchPredictions({
                      ...newFilters,
                      page: 1,
                      limit
                    });
                    // Reset to page 1
                    onPageChange(1);
                  }
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText={t('recommendationDate')}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                isClearable
                customInput={
                  <ReadOnlyInput
                    data-testid={isMobile ? "mobile-date-picker" : "desktop-date-picker"}
                    className="p-[5px] sm:min-w-[180px] border border-solid border-[#c2c7d0] shadow-none min-w-full border border-[#c2c7d0] shadow-[0px_0px_4px_#0000001a] rounded-md px-3 pr-8 cursor-pointer"
                    style={{ caretColor: 'transparent' }}
                  />
                }
              />
              <CalendarIcon className="absolute right-6 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            </div>
          </div>

          <Select
            value={selectedStatus}
            onValueChange={(value) => setSelectedStatus(value)}
            aria-label={t('status')}
          >
            <SelectTrigger 
              className="w-full sm:min-w-[180px] bg-white border-[#c2c7d0]" 
              data-testid={isMobile ? "mobile-status-select" : "desktop-status-select"}
            >
              <span className={!selectedStatus ? 'text-gray-400' : ''}>
                {selectedStatus
                  ? t(PREDICTION_STATUS_OPTIONS.find(opt => opt.value === selectedStatus)?.label || '')
                  : t('status')
                }
              </span>
            </SelectTrigger>
            <SelectContent>
              {PREDICTION_STATUS_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>{t(option.label)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-[32px] sm:mt-0 w-full sm:w-auto flex gap-[8px]">
          <Button
            variant="outline"
            className="w-full sm:w-auto px-4 py-2.5 rounded-[40px] border border-solid border-[#165c56] text-tealteal-70"
            onClick={handleApplyFilters}
            disabled={!selectedDateFilter && !selectedStatus && !selectedProduct}
          >
            {t('apply')}
          </Button>

          <Button
            variant="ghost"
            className="w-full sm:w-auto h-10 px-0 py-2.5 text-neutral-grayneutral-70 font-bold text-[13px]"
            disabled={!selectedDateFilter && !selectedStatus && !selectedProduct}
            onClick={handleClearAll}
          >
            {t('clearAll')}
          </Button>
        </div>
      </div>
    )
  }
  
  const handleFilter = () => {
    setSliderOpen(true)
  }
  
  const handleCloseSlider = () => {
    setSliderOpen(false);
  };

  return (
    <section className="flex flex-col w-full items-start gap-4 px-4">
      <Loader isLoading={isLoading} message={t('loading')} />
      <div className="flex items-center gap-4 w-full flex-wrap justify-between mt-5">
        <h2 className="font-bold text-[#14151c] text-lg tracking-[-0.54px] leading-[22px] font-medium-h4">
          {t('allRecommendations')}
        </h2>
        <div className="sm:flex sm:gap-[16px] sm:items-center flex-col lg:flex-row">
          <div className="hidden sm:block">
            <FiltersContent isMobile={false} />
          </div>
          <div className="fixed bottom-0 right-0 left-0 sm:static bg-white sm:bg-transparent p-[12px] sm:p-0 flex justify-center">
            {/* <Button className="w-full sm:w-auto min-w-[180px] px-6 py-2.5 bg-[#0c612c] text-white rounded-[40px] whitespace-nowrap" */}
                        <Button className="w-full sm:w-auto min-w-[180px] px-6 py-2.5 bg-[#0c612c] text-white rounded-[40px] whitespace-nowrap h-[52px] sm:py-2.5 sm:h-10"
              onClick={handleNewPrediction}>
              {t('newRecommendation')}
            </Button>
          </div>
        </div>

        <div 
          className="block sm:hidden pointer-events-auto relative" 
          onClick={handleFilter} 
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleFilter();
            }
          }}
          role="button"
          tabIndex={0}
          data-testid="filter-icon"
        >
          <Filter />
          {(filtersRef.current.productId || filtersRef.current.status || filtersRef.current.date) && (
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
          )}
        </div>
        <MobileSlider toggleSlider={sliderOpen} onClose={handleCloseSlider}>
          <div>
            <p className="font-semibold my-[16px]">{t('filterBy')}</p>
            <FiltersContent isMobile={true} />
          </div>
        </MobileSlider>

      </div>
      {predictionCards.length > 0 ?
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-[10px] w-full">
            {predictionCards.map((card, index) => (
              <Card 
                key={index} 
                className="rounded bg-white border border-[#dadce3] cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleCardClick(card.id)}
              >
                <CardContent className="p-2.5 flex flex-col gap-3.5">
                  <div className="flex items-center justify-between">
                    <div className="w-[100px] h-[60px]">
                    <img
                      className="h-full w-full object-contain border: 1px solid #C3C3C3 border rounded-sm"
                      alt={card.logoAlt}
                      src={card.logoSrc}
                      />
                      </div>
                    <Badge
                      className={`${PREDICTION_STATUS_CLASSES[card.status as string]} px-1.5 py-1 h-auto font-regular-captions`}
                    >
                      {card.status === "NOT_APPLICABLE" ? "Not Applicable" : t(card.status.toLowerCase())}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-3.5 w-full">
                    <div className="flex-1">
                      <div className="text-[#969795] text-xs font-regular-small-body">
                        {t('landPreparedOn')}
                      </div>
                      <div className="text-secondary-900 font-medium-body-1-medium">
                        {card.landPreparedDate}
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="text-[#969795] text-xs font-regular-small-body">
                        {t('transplantDate')}
                      </div>
                      <div className="text-secondary-900 font-medium-body-1-medium">
                        {card.transplantDate}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3.5 w-full">
                    <div className="flex-1">
                      <div className="text-[#969795] text-xs font-regular-small-body">
                        {t('fieldName')}
                      </div>
                      <div className="text-secondary-900 font-medium-body-1-medium cursor-pointer" title={card.name}>
                        {card.name.length > 8 ? `${card.name.slice(0, 8)}..` : card.name}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-[#969795] text-xs font-regular-small-body">
                        {t('createdOn')}
                      </div>
                      <div className="text-secondary-900 font-medium-body-1-medium">
                        {card.createdAt}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 p-2.5 bg-[#3D8156] rounded">
                    <CalendarIcon className="w-6 h-6 text-white" />
                    <div className="flex-1">
                      <div className="text-white text-xs font-regular-small-body">
                        {t('recommendedApplicationDate')}
                      </div>
                      <div className="text-white font-medium-body-1-medium">
                        {card.applicationRecommendation}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </> :
        <div data-testid="no-data-component" className="w-full">
          <NoDataComponent />
        </div>}
    </section>
  );
};